import { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMvpPartners } from '@/hooks/useMvpPartners';
import './DealerStep.scss';
import { Loader } from '../../Loader/Loader';

export interface DealerFormData {
  dealerCompanyId: string;
  dealerRepresentativeId: string;
  dealerCompanyRemoteId: string;
  dealerRepresentativeRemoteId: string;
}

export interface DealerStepRef {
  submitForm: () => boolean;
  isValid: () => boolean;
  getFormData: () => DealerFormData;
}

export const DealerStep = forwardRef<DealerStepRef, { initialData?: Partial<DealerFormData> }>((props, ref) => {
  const { t } = useTranslation('auth');
  const { register, formState: { errors }, getValues, setValue, watch } = useForm<DealerFormData>({
    defaultValues: props.initialData,
  });

  // Fetch MVP partners for dealer companies
  const { data: mvpPartners = [], isLoading: isLoadingDealerCompanies, isError: dealerCompaniesError, retailerOptions } = useMvpPartners();

  // Extract dealer representatives from MVP partners' contacts
  // We'll use the contacts from the MVP partners data instead of a separate query
  const dealerRepresentatives = mvpPartners.flatMap(partner => 
    partner.contacts?.filter(contact => 
      !contact.hasLeftCompany && 
      ['Principal', 'Store Manager', 'Sales Rep'].includes(contact.dealerFunction)
    ).map(contact => ({
      ...contact,
      dealerCompanyId: partner.id,
      dealerCompanyRemoteId: partner.remoteObjectId,
      dealerCompanyName: partner.name
    })) || []
  );
  
  // Watch for changes to the dealer company selection
  const selectedDealerCompanyId = watch('dealerCompanyId');
  
  // Reset sales representative when dealer changes
  useEffect(() => {
    if (selectedDealerCompanyId) {
      // Only reset if we have a value (not on initial load)
      setValue('dealerRepresentativeId', '');
      setValue('dealerRepresentativeRemoteId', '');
    }
  }, [selectedDealerCompanyId, setValue]);
  
  // We'll use the same loading state as MVP partners for representatives

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      // Dealer fields are optional, so just check if there are any validation errors
      // (there shouldn't be any since fields are not required)
      if (Object.keys(errors).length > 0) {
        return false;
      }
      return true;
    },
    isValid: () => {
      return Object.keys(errors).length === 0;
    },
    getFormData: () => {
      // Get current form values
      const values = getValues();
      
      // If we have IDs but no remoteObjectIds, try to set them now
      if (values.dealerCompanyId && !values.dealerCompanyRemoteId) {
        // For retailerOptions, the value is already the remoteObjectId
        const option = retailerOptions.find(opt => opt.value === values.dealerCompanyId);
        if (option) {
          values.dealerCompanyRemoteId = option.value;
        }
      }
      
      if (values.dealerRepresentativeId && !values.dealerRepresentativeRemoteId) {
        // For representatives, the value is already the remoteObjectId
        const rep = dealerRepresentatives.find(r => r.remoteObjectId === values.dealerRepresentativeId);
        if (rep) {
          values.dealerRepresentativeRemoteId = rep.remoteObjectId;
        }
      }
      
      return values;
    }
  }));

  // Show loading state
  if (isLoadingDealerCompanies) {
    return <Loader variant="overlay" />;
  }

  // Show error state
  if (dealerCompaniesError) {
    return (
      <div className="dealer-step">
        <div className="dealer-step__error">
          <p>{t('registration.dealer.error')}</p>
          <p className="dealer-step__error-details">
            {t('registration.dealer.errorDetails')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dealer-step">
      <div className="dealer-step__header">
        <h2 className="dealer-step__title">{t('registration.dealer.title')}</h2>
        <p className="dealer-step__description">{t('registration.dealer.searchDescription')}</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="dealer-step__form">
        <div className="dealer-step__fields">
          <div className="form-field">
            <label className="form-field__label" htmlFor="dealerCompanyId">
              {t('registration.dealer.primaryDealer')}
            </label>
            <select
              id="dealerCompanyId"
              {...register('dealerCompanyId')}
              className={`form-field__select ${errors.dealerCompanyId ? 'form-field__select--error' : ''}`}
              onChange={(e) => {
                const value = e.target.value;
                setValue('dealerCompanyId', value);
                // Find the selected company and set its remoteObjectId
                const selectedCompany = mvpPartners.find(partner => partner.remoteObjectId === value || partner.id.toString() === value);
                if (selectedCompany && selectedCompany.remoteObjectId) {
                  setValue('dealerCompanyRemoteId', selectedCompany.remoteObjectId);
                } else {
                  console.warn('No remoteObjectId found for company:', selectedCompany);
                }
              }}
            >
              <option value="">{t('registration.dealer.selectDealerPlaceHolder')}</option>
              {retailerOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.dealerCompanyId && (
              <span className="form-field__error">
                {t('registration.dealer.primaryDealerRequired')}
              </span>
            )}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="dealerRepresentativeId">
              {t('registration.dealer.salesRepresentative')}
              <span className="form-field__label-optional">
                {t('registration.dealer.multipleSelection')}
              </span>
            </label>
            <select
              id="dealerRepresentativeId"
              {...register('dealerRepresentativeId')}
              className={`form-field__select ${errors.dealerRepresentativeId ? 'form-field__select--error' : ''}`}
              disabled={!watch('dealerCompanyId')}
              onChange={(e) => {
                const value = e.target.value;
                setValue('dealerRepresentativeId', value);
                // Find the selected representative and set its remoteObjectId
                const selectedRep = dealerRepresentatives.find(rep => rep.remoteObjectId === value);
                if (selectedRep && selectedRep.remoteObjectId) {
                  setValue('dealerRepresentativeRemoteId', selectedRep.remoteObjectId);
                } else {
                  console.warn('No remoteObjectId found for representative:', selectedRep);
                }
              }}
            >
              <option value="">{t('registration.dealer.selectRepresentativePlaceHolder')}</option>
              {dealerRepresentatives
                .filter(representative => {
                  const selectedDealerId = getValues('dealerCompanyId');
                  // Only show representatives from the selected dealer
                  return representative.dealerCompanyRemoteId === selectedDealerId || 
                         representative.dealerCompanyId.toString() === selectedDealerId;
                })
                .map((representative) => (
                  <option key={representative.remoteObjectId} value={representative.remoteObjectId}>
                    {`${representative.firstname} ${representative.lastname} - ${representative.dealerFunction}`}
                  </option>
                ))}
            </select>
            {errors.dealerRepresentativeId && (
              <span className="form-field__error">
                {t('registration.dealer.salesRepresentativeRequired')}
              </span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
});
